import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import { format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

import { purpleAirIdToLocalId } from '../utils/constants';
import { downloadCSV } from '../utils/downloadCSV';

const COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#6366f1',
  '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4'
];

const TIME_RANGES = [
  { value: '1h', label: 'Last Hour' },
  { value: '3h', label: 'Last 3 Hours' },
  { value: '6h', label: 'Last 6 Hours' },
  { value: '12h', label: 'Last 12 Hours' },
  { value: '1d', label: 'Last Day' },
  { value: '3d', label: 'Last 3 Days' },
  { value: '1w', label: 'Last Week' }
];

const CustomTooltip = ({ active, payload, label, viewMode }) => {
  if (active && payload && payload.length) {
    let formattedDate;
    try {
      if (viewMode === 'historical') {
        formattedDate = format(parseISO(label), 'MMM dd, yyyy HH:mm');
      } else {
        formattedDate = label;
      }
    } catch (error) {
      formattedDate = label;
    }

    return (
      <div className="bg-white p-2 border rounded shadow-lg text-xs">
        <p className="font-medium mb-1">{formattedDate}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-1" style={{ color: entry.color }}>
            <span className="font-medium">{entry.name}:</span>
            <span>{entry.value?.toFixed(1)} µg/m³</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ViewModeToggle = ({ viewMode, onViewModeChange }) => (
  <div className="flex items-center bg-gray-100 rounded-lg p-1 text-sm">
    <button
      onClick={() => onViewModeChange('current')}
      className={`px-3 py-1 rounded ${viewMode === 'current'
        ? 'bg-blue-50 text-blue-600'
        : 'text-gray-600 hover:text-gray-800'
        }`}
    >
      Current
    </button>
    <button
      onClick={() => onViewModeChange('historical')}
      className={`px-3 py-1 rounded ${viewMode === 'historical'
        ? 'bg-blue-50 text-blue-600'
        : 'text-gray-600 hover:text-gray-800'
        }`}
    >
      Historical
    </button>
  </div>
);

const ChartToggle = ({ locationId, sensorIds, chartData, viewMode, onViewModeChange, timeRange, onTimeRangeChange }) => {
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [cachedData, setCachedData] = useState({
    historical: new Map(),
    current: new Map()
  });

  const getCacheKey = (mode, start, end) => `${mode}-${start?.toISOString()}-${end?.toISOString()}`;

  const getSensorPosition = (sensorId) => {
    const sensorInfo = purpleAirIdToLocalId[sensorId];
    return sensorInfo ? sensorInfo.position : `Sensor ${sensorId}`;
  };

  useEffect(() => {
    // Inside ChartToggle.js useEffect
    const fetchHistorical = async () => {
      if (viewMode === 'historical' && locationId) {
        const cacheKey = getCacheKey(viewMode, startDate, endDate);
        if (cachedData.historical.has(cacheKey)) {
          setHistoricalData(cachedData.historical.get(cacheKey));
          return;
        }

        setLoading(true);
        try {
          const promises = sensorIds.map(async sensorId => {
            try {
              const response = await fetch(
                `${process.env.REACT_APP_API_URL}/sensors/historical?${new URLSearchParams({
                  sensorId,
                  startDate: startDate.toISOString().split('T')[0],
                  endDate: endDate.toISOString().split('T')[0]
                })}`
              );

              if (!response.ok) {
                console.warn(`Sensor ${sensorId} returned ${response.status}. Skipping.`);
                return null;
              }

              return await response.json();
            } catch (error) {
              console.warn(`Failed to fetch data for sensor ${sensorId}:`, error);
              return null;
            }
          });

          const allSensorData = await Promise.all(promises);
          const combinedData = new Map();

          allSensorData.forEach((sensorData, index) => {
            if (!sensorData) return; // Skip null responses from failed fetches

            const sensorId = sensorIds[index];
            sensorData.forEach(dayData => {
              if (!dayData.readings) return;

              dayData.readings.forEach(reading => {
                const timestamp = reading.timestamp;
                if (!combinedData.has(timestamp)) {
                  combinedData.set(timestamp, {
                    timestamp,
                    date: format(parseISO(timestamp), 'yyyy-MM-dd HH:mm'),
                  });
                }

                const entry = combinedData.get(timestamp);
                entry[`sensor${sensorId}`] = reading.pm25;

                // Recalculate average excluding null/undefined values
                const values = Object.entries(entry)
                  .filter(([key, value]) =>
                    key.startsWith('sensor') &&
                    typeof value === 'number' &&
                    !isNaN(value)
                  )
                  .map(([, value]) => value);

                if (values.length > 0) {
                  entry.average = values.reduce((a, b) => a + b, 0) / values.length;
                }
              });
            });
          });

          const processedData = Array.from(combinedData.values())
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

          setHistoricalData(processedData);
          setCachedData(prev => ({
            ...prev,
            historical: new Map(prev.historical).set(cacheKey, processedData)
          }));
        } catch (error) {
          console.error('Error fetching historical data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchHistorical();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, locationId, sensorIds, startDate, endDate]);

  useEffect(() => {
    setCachedData({
      historical: new Map(),
      current: new Map()
    });
  }, [locationId]);

  const currentData = viewMode === 'current' ? chartData : historicalData;
  const totalReadings = currentData?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />

        <div className="flex items-center gap-4">
          {viewMode === 'current' ? (
            <select
              value={timeRange}
              onChange={(e) => onTimeRangeChange(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm"
            >
              {TIME_RANGES.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-2">
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                className="px-2 py-1 border rounded text-sm w-[5.8rem]"
              />
              <span className="text-sm">to</span>
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                className="px-2 py-1 border rounded text-sm w-[5.8rem]"
              />
            </div>
          )}

          <button
            onClick={() => downloadCSV(
              currentData,
              '',
              startDate,
              endDate,
              sensorIds,
              viewMode
            )}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
            title="Download data as CSV"
          >
            <Download size={16} />
            <span>CSV</span>
          </button>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '300px' }}>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <ResponsiveContainer width="98%" height="100%">
            {viewMode === 'current' ? (
              <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11 }} />
                <YAxis
                  label={{
                    value: 'PM2.5',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 10,
                    fontSize: 11
                  }}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={(props) => <CustomTooltip {...props} viewMode="current" />} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                {sensorIds.map((sensorId, index) => (
                  <Line
                    key={sensorId}
                    type="monotone"
                    dataKey={sensorId}
                    stroke={COLORS[index % COLORS.length]}
                    name={getSensorPosition(sensorId)}
                    dot={false}
                    strokeWidth={1.5}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="average"
                  stroke="#000000"
                  name="Average"
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  dot={false}
                />
              </LineChart>
            ) : (
              <LineChart
                data={historicalData}
                margin={{ top: 5, right: 30, bottom: 5, left: -20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  label={{
                    value: 'PM2.5 (µg/m³)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 10,
                    fontSize: 11
                  }}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={(props) => <CustomTooltip {...props} viewMode="historical" />} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                {sensorIds.map((sensorId, index) => (
                  <Line
                    key={sensorId}
                    type="monotone"
                    dataKey={`sensor${sensorId}`}
                    name={getSensorPosition(sensorId)}
                    stroke={COLORS[index % COLORS.length]}
                    dot={false}
                    strokeWidth={1.5}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="average"
                  name="Average"
                  stroke="#000000"
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  dot={false}
                />
                <Brush
                  dataKey="date"
                  height={20}
                  stroke="#aaa"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>

      <div className="text-sm text-gray-600">
        Total readings: {totalReadings}
      </div>
    </div>
  );
};

export default ChartToggle;